import React, { useMemo, useCallback } from 'react';
import { useHistory } from 'react-router-dom';
import dayjs from 'dayjs';
import styled from 'styled-components';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { useCalendarData } from '../../../hooks/dentist/useCalendarData';
import { ProcessedJob } from '../../../types/dentist/calendar';
import DentistNavbar from '../../../components/DentistNavbar';
import { LottieLoader } from '../../../components/LottieLoader';
import clsx from 'clsx';
import { JOB_STATUS } from '../../../constants/dentist/jobStatus';
import { isJobActionRequired } from '../../../utils/dentist/calendar/actionRequired';
import { getDentistShiftRoleLabel } from '../../../utils/dentist/shiftRoleLabel';
import { getDentistJobsForDateNavigation } from './navigation';

// Page wrapper for full height background
const PageWrapper = styled.div`
  min-height: 100vh;
  min-height: 100dvh;
  background-color: white;
  display: flex;
  flex-direction: column;
`;

// Container for the main content
const Container = styled.div`
  width: 100%;
  max-width: 800px;
  margin-left: auto;
  margin-right: auto;
  padding: 0 1rem 2rem;
  box-sizing: border-box;
  font-family: var(--fontMain, Poppins), sans-serif;
  background-color: white;
  flex: 1;

  @media (min-width: 768px) {
    max-width: 900px;
    padding-left: 1.5rem;
    padding-right: 1.5rem;
  }

  @media (min-width: 1200px) {
    max-width: 1000px;
    padding-left: 2rem;
    padding-right: 2rem;
  }
`;

// Subheader with back button + centered title
const Subheader = styled.div`
  display: grid;
  grid-template-columns: 36px 1fr 36px;
  align-items: center;
  column-gap: 12px;
  padding: 12px 0;
  margin-bottom: 8px;
`;

const BackButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  background: white;
  border: 1px solid #E5E7EB;
  border-radius: 8px;
  cursor: pointer;
  color: #1F2937;
  transition: all 0.2s;
  
  &:hover {
    background: #F9FAFB;
    border-color: #D1D5DB;
  }
  
  svg {
    width: 20px;
    height: 20px;
  }
`;

const PageTitle = styled.h2`
  font-family: var(--fontMain, Poppins), sans-serif;
  font-weight: 600;
  font-size: 1.25rem;
  color: #05132E;
  margin: 0;
  text-align: center;
`;

const SubheaderSpacer = styled.div`
  width: 36px;
  height: 36px;
`;

const ShiftCard = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  border: 1px solid #E8E8E8;
  border-radius: 12px;
  padding: 1rem;
  margin-bottom: 0.75rem;
  background: #F7F8FA;
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover {
    border-color: var(--primary-blue, #0B4598);
    background: #F0F4F8;
    box-shadow: 0 2px 8px rgba(11, 69, 152, 0.1);
  }
  
  &:active {
    background-color: #E8EDF3;
  }
`;

const ShiftCardContent = styled.div`
  flex: 1;
  min-width: 0;
`;

const ChevronIcon = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  margin-left: 12px;
  flex-shrink: 0;
  color: #1F2937;
  transition: color 0.2s ease, transform 0.2s ease;
  
  ${ShiftCard}:hover & {
    color: var(--primary-blue, #0B4598);
    transform: translateX(2px);
  }
  
  svg {
    width: 20px;
    height: 20px;
  }
`;

const ShiftHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  margin-bottom: 0.5rem;
`;

const StatusIndicator = styled.div<{ status: number }>`
  width: 12px;
  height: 12px;
  border-radius: 50%;
  flex-shrink: 0;
  background-color: ${({ status }) => {
    switch (status) {
      case 1: return '#13CA82'; // Posted/Unfilled - green (matches calendar)
      case 2: return '#F59E0B'; // Custom/Active offers - yellow/orange
      case 3: return '#0B4598'; // Booked - blue (matches calendar)
      case 4: return '#9E9E9E'; // Completed - gray
      case 5: return '#EF4444'; // Expired - red
      default: return '#BDBDBD';
    }
  }};
`;

const ShiftTitle = styled.div`
  font-weight: 600;
  font-size: 1rem;
  color: #05132E;
`;

const ShiftTime = styled.div`
  font-size: 0.9375rem;
  color: #6D6D72;
  margin-bottom: 0.25rem;
`;

const ShiftStatus = styled.div<{ status: number }>`
  font-size: 0.875rem;
  font-weight: 500;
  color: ${({ status }) => {
    switch (status) {
      case 1: return '#13CA82'; // Posted/Unfilled - green
      case 2: return '#F59E0B'; // Custom/Active offers - yellow/orange
      case 3: return '#0B4598'; // Booked - blue
      case 4: return '#9E9E9E'; // Completed - gray
      case 5: return '#EF4444'; // Expired - red
      default: return '#6D6D72';
    }
  }};
`;

const ShiftLocation = styled.div`
  font-size: 0.875rem;
  color: #6D6D72;
  margin-top: 0.5rem;
`;

const ActionBadge = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
  background-color: #EF4444;
  color: #fff;
  font-size: 12px;
  font-weight: bold;
  border-radius: 50%;
  flex-shrink: 0;
  animation: actionPulse 2s infinite;

  @keyframes actionPulse {
    0% { box-shadow: 0 0 0 0 rgba(239, 68, 68, 0.6); }
    70% { box-shadow: 0 0 0 6px rgba(239, 68, 68, 0); }
    100% { box-shadow: 0 0 0 0 rgba(239, 68, 68, 0); }
  }
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 3rem 1rem;
  color: #6D6D72;
  font-size: 1rem;
`;

// T2-5769: loader while we wait for the calendar fetch to finish so the user
// no longer sees the "No shifts posted for this date." empty state during load.
const LoaderHost = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 3rem 1rem;
`;

// T2-6191: red "!" badge when a completed shift has an unpaid invoice OR a
// pending DO feedback survey (shared predicate keeps every surface in sync).
const isActionRequired = (job: ProcessedJob): boolean => isJobActionRequired(job);

const getStatusLabel = (status: number, hasActiveOffers?: boolean): string => {
  switch (status) {
    case 1: return 'Unfilled';
    case 2: return hasActiveOffers ? 'Custom Offer Waiting' : 'Unfilled';
    case 3: return 'Booked / Confirmed';
    case 4: return 'Completed';
    case 5: return 'Expired';
    default: return '';
  }
};

const DentistJobsForDatePage: React.FC = () => {
  const history = useHistory();
  const { dateJobsMap, isLoading } = useCalendarData();
  const state = history.location.state as
    | { dateKey?: string; prefetchedJobs?: ProcessedJob[] }
    | undefined;
  const dateKey = state?.dateKey;
  const prefetchedJobs = state?.prefetchedJobs;

  /**
   * T2-5769: prefer jobs forwarded from the parent calendar so we render the
   * date list instantly. The hook still refreshes in the background and we
   * fall back to its dateJobsMap once it's ready.
   */
  const jobs: ProcessedJob[] = useMemo(() => {
    if (!dateKey) return [];
    const fromMap = dateJobsMap[dateKey];
    if (fromMap && fromMap.length > 0) return fromMap;
    return prefetchedJobs || [];
  }, [dateKey, dateJobsMap, prefetchedJobs]);

  // Only show the loader before any data has rendered (no prefetch + still fetching).
  const showInitialLoader =
    isLoading && (!prefetchedJobs || prefetchedJobs.length === 0) && jobs.length === 0;

  const handleBackNavigation = useCallback(() => {
    history.push({ pathname: 'dentistHome' });
  }, [history]);

  // Get user info for navbar
  const currentUser = (window as any)?.TempStars?.User?.getCurrentUser?.();
  const dentist = currentUser?.dentist;
  const firstName = currentUser?.firstName || '';
  const lastName = currentUser?.lastName || '';
  const photoUrl = currentUser?.photoUrl || dentist?.photoUrl || undefined;

  const handleGetTemp = useCallback(() => {
    history.push({ pathname: 'dentistHome' });
    setTimeout(() => {
      (window as any).TempStars?.App?.goReact?.('postJob');
    }, 100);
  }, [history]);

  const navigateForJob = (job: ProcessedJob): void => {
    const unifiedEnabled = !!(window as any)?.TempStars?.FeatureFlags?.unifiedPostedShiftEnabled;
    const navigation = getDentistJobsForDateNavigation(job, jobs.length, unifiedEnabled);
    if (!navigation) {
      throw new Error(`Invalid job status: ${job.status}`);
    }
    history.push(navigation);
  };

  const dateLabel = dateKey ? dayjs(dateKey).format('MMMM D, YYYY') : 'Selected Date';

  return (
    <PageWrapper>
      <DentistNavbar
        firstName={firstName}
        lastName={lastName}
        photoUrl={photoUrl}
        onGetTempClick={handleGetTemp}
        showGetTempButton={true}
      />
      <Container>
        <Subheader>
          <BackButton onClick={handleBackNavigation} className="no-fastclick" aria-label="Go back">
            <ArrowBackIcon />
          </BackButton>
          <PageTitle>Shifts on {dateLabel}</PageTitle>
          <SubheaderSpacer aria-hidden="true" />
        </Subheader>
        
        {showInitialLoader && (
          <LoaderHost>
            <LottieLoader show={true} />
          </LoaderHost>
        )}

        {!showInitialLoader && (!jobs || jobs.length === 0) && (
          <EmptyState>No shifts posted for this date.</EmptyState>
        )}

        {!showInitialLoader && jobs.map((job, idx) => {
          const type = getDentistShiftRoleLabel(job.type);
          const time = job.shifts && job.shifts[0] 
            ? `${job.shifts[0].postedStart} - ${job.shifts[0].postedEnd}` 
            : 'Time not specified';
          const location = job.address || job.locationName || '';
          const practiceName = (job as any).practiceName || (job as any).locationName || location.split(',')[0] || '';
          const statusLabel = getStatusLabel(job.status, job.hasActiveOffers);

          return (
            <ShiftCard 
              key={job.id} 
              className={clsx('no-fastclick')}
              onClick={() => navigateForJob(job)}
            >
              <ShiftCardContent>
                <ShiftHeader>
                  <StatusIndicator status={job.status} />
                  <ShiftTitle>{practiceName ? `${practiceName} | ${type}` : type}</ShiftTitle>
                  {isActionRequired(job) && <ActionBadge title="Action Required">!</ActionBadge>}
                </ShiftHeader>
                <ShiftTime>{time}</ShiftTime>
                <ShiftStatus status={job.status}>{statusLabel}</ShiftStatus>
                {location && <ShiftLocation>{location}</ShiftLocation>}
              </ShiftCardContent>
              <ChevronIcon>
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="9 18 15 12 9 6" />
                </svg>
              </ChevronIcon>
            </ShiftCard>
          );
        })}
      </Container>
    </PageWrapper>
  );
};

export default DentistJobsForDatePage;

